/**
 * Unified Design Token Module — Single Source of Truth
 *
 * All design tokens for @dramadetector are defined here and consumed by:
 *   - packages/ui/tailwind.config.js (NativeWind utilities)
 *   - packages/ui/src/lib/theme.ts (ThemeColors interface)
 *   - packages/ui/styles/tier-variables.css (CSS custom properties)
 *   - @dramadetector/shared/constants/drama.ts (re-exports for legacy callers)
 *   - @dramadetector/shared/og.ts (OG image rendering)
 *   - AI agent docs / SDUI schemas
 *   - workers/ (OG image, share image generation)
 *
 * Token structure follows the W3C Design Tokens specification:
 *   Primitive → Semantic → Component layers
 *
 * ## How to use
 *
 * **Tailwind config**: import { semantic } and map to theme.extend
 * **React components**: import { drama, semantic } for runtime hex values
 * **CSS vars**: import { cssVars } and generate tier-variables.css
 * **AI agents**: import { agentCatalog } for SDUI / @json-render specs
 *
 * @module @dramadetector/shared/design/tokens
 */

// ============================================================================
// PRIMITIVE TOKENS
// Raw values — no semantic meaning. The foundation layer.
// ============================================================================

export const primitive = {
  // ── Color Palette ──────────────────────────────────────────────────────
  color: {
    white: '#ffffff',
    black: '#000000',

    // Neutral scale (gray)
    gray: {
      50:  '#F9FAFB',
      100: '#F3F4F6',
      200: '#E5E7EB',
      300: '#D1D5DB',
      400: '#9CA3AF',
      500: '#6B7280',
      600: '#4B5563',
      700: '#374151',
      800: '#1F2937',
      900: '#111827',
      950: '#030712',
    },

    // Brand palette
    brand: {
      50:  '#EFF6FF',
      100: '#DBEAFE',
      200: '#BFDBFE',
      300: '#93C5FD',
      400: '#60A5FA',
      500: '#3B82F6',
      600: '#2563EB',
      700: '#1D4ED8',
      800: '#1E40AF',
      900: '#1E3A8A',
    },

    // Drama category colors (authoritative)
    dramaSafe:      '#22c55e',  // Green — score 0-2
    dramaCaution:   '#f59e0b',  // Amber — score 2.1-5
    dramaHigh:      '#f97316',  // Orange — score 5.1-7
    dramaExtreme:   '#ef4444',  // Red — score 7.1-10

    // Tier accents
    accentGold:   '#f59e0b',
    accentBlush:  '#ec4899',

    // UI component accents (switch, toggle)
    accentViolet:       '#A78BFA',
    accentVioletDark:   '#8B5CF6',

    // Status colors
    success: '#10B981',
    warning: '#F59E0B',
    error:   '#EF4444',
  },

  // ── Typography Scale ───────────────────────────────────────────────────
  fontSize: {
    xs:   12,
    sm:   14,
    base: 16,
    lg:   18,
    xl:   20,
    '2xl': 24,
    '3xl': 30,
    '4xl': 36,
  },

  fontWeight: {
    normal:   400,
    medium:   500,
    semibold: 600,
    bold:     700,
    extrabold:800,
  },

  // ── Spacing Scale ──────────────────────────────────────────────────────
  space: {
    0:   0,
    1:   4,
    2:   8,
    3:   12,
    4:   16,
    5:   20,
    6:   24,
    8:   32,
    10:  40,
    12:  48,
    16:  64,
    20:  80,
  },

  // ── Border Radius ──────────────────────────────────────────────────────
  radius: {
    none: 0,
    sm:   4,
    base: 8,
    md:   12,
    lg:   16,
    xl:   24,
    full: 9999,
  },

  // ── Motion ─────────────────────────────────────────────────────────────
  motion: {
    duration: {
      fast:     150,
      normal:   300,
      slow:     500,
      reveal:   700,
    },
    easing: {
      ease:     'ease-in-out',
      spring:   'cubic-bezier(0.34, 1.56, 0.64, 1)',
      linear:   'linear',
    },
  },
} as const;

// ============================================================================
// SEMANTIC TOKENS
// Role-based tokens — reference primitives. Consumed by NativeWind + React.
// ============================================================================

export const semantic = {
  // ── Text Colors ────────────────────────────────────────────────────────
  text: {
    primary:   primitive.color.gray[900],
    secondary: primitive.color.gray[700],
    tertiary:  primitive.color.gray[500],
    inverse:   primitive.color.white,
    brand:     primitive.color.brand[500],
    link:      primitive.color.brand[600],
  },

  // ── Background Colors ──────────────────────────────────────────────────
  bg: {
    surface:      primitive.color.white,
    elevated:     primitive.color.gray[50],
    muted:        primitive.color.gray[100],
    brand:        primitive.color.brand[50],
    brandSubtle:  primitive.color.brand[100],
  },

  // ── Border Colors ──────────────────────────────────────────────────────
  border: {
    subtle:   primitive.color.gray[200],
    default:  primitive.color.gray[300],
    strong:   primitive.color.gray[400],
    brand:    primitive.color.brand[200],
  },

  // ── Status / Feedback ──────────────────────────────────────────────────
  status: {
    success: primitive.color.success,
    warning: primitive.color.warning,
    error:   primitive.color.error,
    info:    primitive.color.brand[500],
  },

  // ── Info/Alert (used in EmailSettings, etc.) ───────────────────────────
  info: {
    bg:     primitive.color.brand[50],
    text:   primitive.color.brand[700],
    border: primitive.color.brand[200],
  },
} as const;

// ============================================================================
// DRAMA TOKENS
// Domain-specific tokens for the DramaDetector product.
// ============================================================================

export const drama = {
  // ── Category Colors (authoritative source) ─────────────────────────────
  category: {
    safe:          primitive.color.dramaSafe,
    caution:       primitive.color.dramaCaution,
    highDrama:     primitive.color.dramaHigh,
    extremeDrama:  primitive.color.dramaExtreme,
  },

  // ── Category Labels ────────────────────────────────────────────────────
  categoryLabel: {
    safe:          'Safe',
    caution:       'Caution',
    highDrama:     'High Drama',
    extremeDrama:  'Extreme Drama',
  },

  // ── Score Thresholds (logical, no color) ───────────────────────────────
  thresholds: {
    safeMax:        2.0,
    cautionMin:     2.1,
    cautionMax:     5.0,
    highDramaMin:   5.1,
    highDramaMax:   7.0,
    extremeDramaMin: 7.1,
  },

  // ── OG Image Category Colors (RGB values for templates) ────────────────
  ogCategory: {
    safe:         { bg: '#F0FDF4', text: '#166534', accent: '#22C55E' },
    caution:      { bg: '#FFFBEB', text: '#92400E', accent: '#F59E0B' },
    highDrama:    { bg: '#FEF2F2', text: '#991B1B', accent: '#EF4444' },
    extremeDrama: { bg: '#FEF2F2', text: '#7F1D1D', accent: '#DC2626' },
  },

  // ── Share Image Dimensions ─────────────────────────────────────────────
  shareFormat: {
    horizontal: { width: 1200, height: 630 },
    square:     { width: 1080, height: 1080 },
    portrait:   { width: 1080, height: 1350 },
    reel:       { width: 1080, height: 1920 },
  },
} as const;

// ============================================================================
// TIER TOKENS
// Tier-adaptive styling (free / drama+ / dramax).
// ============================================================================

export const tiers = {
  free: {
    label: 'Free',
    accent: primitive.color.brand[500],
    premium: false,
  },
  dramaPlus: {
    label: 'Drama+',
    accent: primitive.color.accentGold,
    premium: true,
  },
  dramaX: {
    label: 'DramaX',
    accent: primitive.color.accentBlush,
    premium: true,
  },
} as const;

export type TierId = keyof typeof tiers;

// ============================================================================
// TIER STYLES (json-render / SDUI)
// Component-level styling config for each tier's image generation.
// ============================================================================

export const tierStyles: Record<TierId, {
  cardBg: string;
  cardBorder: string;
  badgeBg: string;
  badgeText: string;
  accent: string;
}> = {
  free: {
    cardBg: '#FFFFFF',
    cardBorder: '#E5E7EB',
    badgeBg: '#F3F4F6',
    badgeText: '#374151',
    accent: primitive.color.brand[500],
  },
  dramaPlus: {
    cardBg: '#1F2937',
    cardBorder: '#374151',
    badgeBg: '#F59E0B20',
    badgeText: '#F59E0B',
    accent: primitive.color.accentGold,
  },
  dramaX: {
    cardBg: '#0F172A',
    cardBorder: '#1E293B',
    badgeBg: '#EC489920',
    badgeText: '#EC4899',
    accent: primitive.color.accentBlush,
  },
};

// ============================================================================
// CSS CUSTOM PROPERTIES
// Flat map for CSS vars (used by tier-variables.css and NativeWind).
// ============================================================================

export const cssVars = {
  // Colors
  'color-primary-rgb':        '138 43 226',
  'color-safe-rgb':           '34 197 94',      // #22C55E — matches primitive
  'color-caution-rgb':        '245 158 11',     // #F59E0B
  'color-danger-rgb':         '239 68 68',      // #EF4444
  'color-brand-rgb':          '59 130 246',     // #3B82F6

  // Typography
  'font-display':  "'Inter', sans-serif",
  'font-body':     "'Inter', sans-serif",
  'font-narrator': "'Playfair Display', serif",

  // Spacing (CSS rem scale — tier-invariant)
  'space-1': '0.25rem',
  'space-2': '0.5rem',
  'space-3': '0.75rem',
  'space-4': '1rem',
  'space-6': '1.5rem',
  'space-8': '2rem',
  'space-10': '2.5rem',
  'space-12': '3rem',

  // Border radius
  'radius':    '0.5rem',
  'radius-sm': '0.25rem',
  'radius-lg': '1rem',

  // Motion
  'motion-duration': '300ms',
  'motion-duration-reveal': '700ms',
  'motion-timing': 'ease-in-out',
  'motion-spring': 'cubic-bezier(0.34, 1.56, 0.64, 1)',
} as const;

// ============================================================================
// AGENT CATALOG
// Machine-readable token map for AI / SDUI / @json-render specs.
// ============================================================================

export const agentCatalog = {
  name: 'dramadetector-design-tokens',
  version: '1.0.0',
  layers: ['primitive', 'semantic', 'drama', 'tiers'],
  dramaCategories: Object.keys(drama.category),
  dramaThresholds: drama.thresholds,
  shareFormats: Object.keys(drama.shareFormat),
  tierIds: Object.keys(tiers),
  semanticTextColors: Object.keys(semantic.text),
  semanticBgColors: Object.keys(semantic.bg),
} as const;

// ============================================================================
// COMPATIBILITY RE-EXPORTS
// Legacy callers import from @dramadetector/shared/constants/drama and
// @dramadetector/shared/og. These re-exports maintain backward compatibility
// while the canonical source is this module.
// ============================================================================

/** @deprecated Use drama.category directly */
export const CATEGORY_COLORS = {
  safe:         drama.ogCategory.safe,
  caution:      drama.ogCategory.caution,
  high_drama:   drama.ogCategory.highDrama,
  extreme_drama: drama.ogCategory.extremeDrama,
} as const;

/** @deprecated Use drama.categoryLabel directly */
export const CATEGORY_LABELS = drama.categoryLabel;

/** @deprecated Use drama.thresholds directly */
export const DRAMA_THRESHOLDS = {
  SAFE_MAX:         drama.thresholds.safeMax,
  CAUTION_MIN:      drama.thresholds.cautionMin,
  CAUTION_MAX:      drama.thresholds.cautionMax,
  HIGH_DRAMA_MIN:   drama.thresholds.highDramaMin,
  HIGH_DRAMA_MAX:   drama.thresholds.highDramaMax,
  EXTREME_DRAMA_MIN: drama.thresholds.extremeDramaMin,
} as const;
